import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useParams, useLocation} from '@github-ui/react-router'
import {useNavigate, useSearchParams} from '@github-ui/use-navigate'
import {PageHeader, PageLayout, Pagination} from '@primer/react'

import {ArtifactsList} from '../components/ArtifactsList'
import {SidebarNav} from '../components/SidebarNav'
import {artifactRegistryIndexRoute} from './artifact-registry-route'
import {Blankslate} from '@primer/react/experimental'
import {PackageIcon} from '@primer/octicons-react'
import styles from './ArtifactRegistryIndex.module.css'
import {useCallback, useEffect, useRef, useState, type MouseEvent} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Filter, type FilterQuery} from '@github-ui/filter'
import {announce} from '@github-ui/aria-live'
import {DEFAULT_SORT, type SortOrderType} from '../types'
import {parseSortFromQuery, setSortInQuery} from '../helpers/query-sort'

const emptyProviders: [] = []

export function ArtifactRegistryIndex() {
  const {data: payload} = useRouteQuery(artifactRegistryIndexRoute, 'mainQuery')
  const routeParams = useParams<{org: string}>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()

  const {artifacts, pageInfo, totalCount} = payload
  const org = routeParams.org as string
  const isV2 = isFeatureEnabled('artifact_ui_v2')
  const useClientPagination = isV2

  const artifactsListRef = useRef<HTMLUListElement | undefined>(undefined)

  const q = isV2 ? searchParams.get('q') || '' : ''
  const [filterValue, setFilterValue] = useState(q)

  const currentSort: SortOrderType = parseSortFromQuery(q)

  // Support both old totalCount and new pageInfo.totalCount
  const artifactsTotalCount = totalCount ?? pageInfo?.totalCount ?? 0

  // Sync filter value when URL changes (e.g., browser back/forward)
  useEffect(() => {
    setFilterValue(q)
  }, [q])

  // Only show pagination if pageInfo exists and has multiple pages
  const shouldShowPagination = pageInfo && pageInfo.totalPages > 1

  const updateQueryParam = useCallback(
    (newQuery: string) => {
      setSearchParams(prev => {
        const params = new URLSearchParams(prev)
        if (newQuery) {
          params.set('q', newQuery)
        } else {
          params.delete('q')
        }
        params.delete('page')
        return params
      })
    },
    [setSearchParams],
  )

  const handleFilterSubmit = useCallback(
    (request: FilterQuery) => updateQueryParam(request.raw.trim()),
    [updateQueryParam],
  )

  const handleSortChange = useCallback(
    (newSort: SortOrderType) => updateQueryParam(setSortInQuery(q, newSort !== DEFAULT_SORT ? newSort : '')),
    [q, updateQueryParam],
  )

  const activePage = pageInfo?.currentPage ?? 1

  // Announce search results to screen readers
  const isInitialRenderRef = useRef(true)
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false
      return
    }
    if (!q || artifactsTotalCount === 0) return
    const message = artifactsTotalCount === 1 ? '1 artifact found' : `${artifactsTotalCount} artifacts found`
    announce(message, {assertive: true})
  }, [q, artifactsTotalCount])

  const buildPageSearch = useCallback(
    (page: number) => {
      const params = new URLSearchParams(searchParams)
      if (page > 1) {
        params.set('page', page.toString())
      } else {
        params.delete('page')
      }
      const search = params.toString()
      return search ? `?${search}` : ''
    },
    [searchParams],
  )

  const hrefBuilder = buildPageSearch

  const onPageChange = useCallback(
    (e: MouseEvent, page: number) => {
      if (!useClientPagination) return
      // Allow modified clicks (cmd-click, ctrl-click, etc.) to use default browser behavior (e.g., open in new tab)
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return

      e.preventDefault()
      artifactsListRef.current?.focus()
      window.scrollTo({top: 0})
      navigate({pathname: location.pathname, search: buildPageSearch(page)})
    },
    [useClientPagination, buildPageSearch, navigate, location.pathname],
  )

  const hasSearchQuery = q.length > 0

  const filterComponent = (
    <Filter
      id="artifact-registry-filter"
      className="tmp-mb-3"
      label="Filter artifacts"
      placeholder="Search"
      variant="input" // remove when adding filtering options beyond search
      filterValue={filterValue}
      onChange={setFilterValue}
      onSubmit={handleFilterSubmit}
      providers={emptyProviders}
    />
  )

  const emptyState = (
    <Blankslate>
      <Blankslate.Visual>
        <PackageIcon size="medium" />
      </Blankslate.Visual>
      <Blankslate.Heading>Track artifacts from build through deployment</Blankslate.Heading>
      <Blankslate.Description className="text-center">
        Link artifacts built on GitHub Actions to external registries and environments with the artifact metadata API
      </Blankslate.Description>
      <Blankslate.SecondaryAction href="https://docs.github.com/en/rest/orgs/artifact-metadata?apiVersion=2022-11-28">
        Learn more about the artifact metadata API
      </Blankslate.SecondaryAction>
    </Blankslate>
  )

  return (
    <PageLayout padding="none" containerWidth="full" columnGap="none">
      <PageLayout.Pane
        position="start"
        divider="line"
        resizable // adding resizable for responsiveness on narrow viewports, otherwise width is constrained
        width={{min: '336px', default: '336px', max: '360px'}}
        className={styles.NavPane}
      >
        <SidebarNav organization={org} selectedTab="linked_artifacts" />
      </PageLayout.Pane>
      <PageLayout.Content as="div" padding="normal">
        {artifacts.length === 0 && !hasSearchQuery ? (
          emptyState
        ) : (
          <>
            <PageHeader aria-label="Linked artifacts" as="h3" className="tmp-mb-4" hasBorder>
              <PageHeader.TitleArea>
                <PageHeader.Title>Linked artifacts</PageHeader.Title>
              </PageHeader.TitleArea>
            </PageHeader>
            {isV2 && filterComponent}
            <ArtifactsList
              org={org}
              artifacts={artifacts}
              totalCount={artifactsTotalCount}
              isV2={isV2}
              listRef={artifactsListRef}
              currentSort={currentSort}
              onSortChange={handleSortChange}
            />
            {shouldShowPagination && (
              <div className="d-flex flex-justify-center tmp-mt-4">
                <Pagination
                  pageCount={pageInfo.totalPages}
                  currentPage={activePage}
                  hrefBuilder={hrefBuilder}
                  onPageChange={onPageChange}
                />
              </div>
            )}
          </>
        )}
      </PageLayout.Content>
    </PageLayout>
  )
}
